/**
 * Builds the downloadable XLSX import template.
 *
 * Layout is fixed across document types: rows 1-3 are an info block (title,
 * guidance, generated-on note), row 4 is the data header, rows 5+ are the
 * worked examples. The sheet freezes at row 4 so both stay visible while
 * the user types.
 */

/**
 * Headers whose example values must stay numeric so the sheet doesn't
 * render them as left-aligned text. Everything else is written verbatim,
 * and anything in `textHeaders` is additionally forced to text format.
 */
const NUMERIC_HEADERS = ["quantity", "rate-rupees", "tax-percent", "discount-percent"];

/** Everything a document type needs to describe its own template sheet. */
export interface XlsxTemplateSpec {
  /** Canonical template headers, in column order. */
  headers: readonly string[];
  /** Worked example rows as comma-joined cell values, one per sheet row. */
  exampleRows: readonly string[];
  /** Worksheet tab name, e.g. `Invoices`. */
  sheetName: string;
  /** Row 1 title, e.g. `Flint · Invoice import template`. */
  title: string;
  /** Row 2 guidance line — how rows map to documents. */
  guidance: string;
  /** Header → column width. Tuned so nothing renders as `###` on open. */
  columnWidths: Record<string, number>;
  /**
   * Cells that must render as text, not a number or date, so Excel doesn't
   * corrupt them — IDs, 4-digit HSN codes, and state codes with a leading
   * zero like `03` (Punjab).
   */
  textHeaders: ReadonlySet<string>;
  /** Downloaded file name, including the `.xlsx` extension. */
  fileName: string;
}

/** Spreadsheet column letter for a 0-based index: 0 → A, 26 → AA. */
function columnLetter(index: number): string {
  let n = index;
  let out = "";
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

/** Builds the template workbook and triggers a browser download. */
export async function downloadXlsxTemplate(spec: XlsxTemplateSpec): Promise<void> {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = "Flint";
  wb.created = new Date();
  const ws = wb.addWorksheet(spec.sheetName, {
    // Freeze at row 4 so the info block + header row stay visible.
    views: [{ state: "frozen", ySplit: 4 }],
  });

  ws.columns = spec.headers.map((h) => ({ width: spec.columnWidths[h] ?? 14 }));

  // ── Rows 1-3 — info block ─────────────────────────────────────────
  const lastCol = columnLetter(spec.headers.length - 1);
  ws.mergeCells(`A1:${lastCol}1`);
  const title = ws.getCell("A1");
  title.value = spec.title;
  title.font = { name: "Calibri", size: 16, bold: true, color: { argb: "FF0F172A" } };
  title.alignment = { vertical: "middle", horizontal: "left" };
  ws.getRow(1).height = 28;

  ws.mergeCells(`A2:${lastCol}2`);
  const guidance = ws.getCell("A2");
  guidance.value = spec.guidance;
  guidance.font = { italic: true, color: { argb: "FF64748B" } };
  guidance.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  ws.getRow(2).height = 22;

  ws.mergeCells(`A3:${lastCol}3`);
  const generated = ws.getCell("A3");
  generated.value = `Generated ${new Date().toISOString().slice(0, 10)} · replace {customer-uuid} with real customer IDs from the Customers list`;
  generated.font = { italic: true, size: 10, color: { argb: "FF94A3B8" } };
  generated.alignment = { vertical: "middle", horizontal: "left" };

  // ── Row 4 — data header ───────────────────────────────────────────
  const headerRow = ws.getRow(4);
  spec.headers.forEach((label, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = label;
    cell.font = { bold: true, color: { argb: "FF0F172A" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } };
    cell.alignment = { vertical: "middle", horizontal: "left" };
    cell.border = { bottom: { style: "thin", color: { argb: "FF94A3B8" } } };
  });
  headerRow.height = 22;

  // ── Rows 5+ — example data ────────────────────────────────────────
  spec.exampleRows.forEach((raw, rowIdx) => {
    const cells = raw.split(",");
    const dataRow = ws.getRow(5 + rowIdx);
    spec.headers.forEach((header, colIdx) => {
      const cell = dataRow.getCell(colIdx + 1);
      const value = cells[colIdx] ?? "";
      if (spec.textHeaders.has(header)) {
        cell.value = value;
        cell.numFmt = "@";
      } else if (NUMERIC_HEADERS.includes(header)) {
        const n = Number(value);
        cell.value = Number.isFinite(n) ? n : value;
        if (header === "rate-rupees") cell.numFmt = "#,##,##0.00";
        else if (header === "tax-percent" || header === "discount-percent") cell.numFmt = "0.00";
      } else {
        cell.value = value;
      }
    });
  });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = spec.fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
